import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Question, ResponsePayload, QuestionType } from '../types/index';
import { getTestId, getQuestionTypeTestId } from '../utils/testUtils';
import { RevealCard } from './RevealCard';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { 
  Box, 
  Typography, 
  TextField, 
  FormControlLabel, 
  Checkbox, 
  Paper, 
  Card, 
  Alert, 
  Button, 
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar
} from '@mui/material';
import { styled, Theme } from '@mui/material/styles';
import { responses, submitBugReport } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { VoiceRecorder } from './questions/VoiceRecorder';
import { MultipleChoiceQuestion } from './questions/MultipleChoiceQuestion';
import { MultipleSelectQuestion } from './questions/MultipleSelectQuestion';
import { FreeResponseQuestion } from './questions/FreeResponseQuestion';
import { SubmitButton } from './questions/SubmitButton';
import { TextOrAudioResponseQuestion } from './questions/TextOrAudioResponseQuestion';
import { DescriptionQuestion } from './questions/DescriptionQuestion';
import { RatingQuestion } from './questions/RatingQuestion';
import { DragAndDropQuestion } from './questions/DragAndDropQuestion';
import { HorizontalDragDropQuestion } from './questions/HorizontalDragDropQuestion';
import { VideoPlayerQuestion } from './questions/VideoPlayerQuestion';
import { PDFQuestion } from './questions/PDFQuestion';
import { StyledBooleanQuestion } from './questions/StyledBooleanQuestion';
import { CallbackSummaryQuestion } from './questions/CallbackSummaryQuestion';
import { CustomRadarChart } from './RadarChart';
import { images } from '../services/api';
import { imageCache } from '../utils/imageCache';

interface QuestionCardProps {
  question: Question;
  onSubmit: (response: ResponsePayload) => Promise<void>;
  isSubmitting?: boolean;
  flowName?: string;
  onProgressWithoutSubmitting?: () => Promise<void>; // Function to progress to next question without submitting a response
}

// Create a type for the StyledCard props
interface StyledCardProps {
  isPdf?: boolean;
  isMobile?: boolean;
}

const StyledCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isMobile' && prop !== 'isPdf',
})<StyledCardProps>(({ theme, isPdf, isMobile }) => ({
  backgroundColor: 'transparent',  // Changed to transparent
  padding: isMobile ? theme.spacing(3) : theme.spacing(6),
  width: '100%',
  maxWidth: isMobile ? '95vw' : '80vw', // Adjust width for mobile
  margin: '0 auto', // Center the card
  transition: 'all 0.3s ease',
  position: 'relative',
}));

interface QuestionTextProps {
  isMobile?: boolean;
}

const QuestionText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<QuestionTextProps>(({ theme, isMobile }) => ({
  fontSize: isMobile ? '1.25rem' : '1.5rem',
  fontWeight: 500,
  marginBottom: isMobile ? theme.spacing(3) : theme.spacing(4),
  color: theme.palette.text.primary,
  lineHeight: 1.4,
}));

const ImageComponent = ({ imageUrl }: { imageUrl: string }) => {
  const [signedUrl, setSignedUrl] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  // Helper to fetch the signed URL with a 10s timeout
  const fetchSignedUrlWithTimeout = async () => {
    let didCancel = false;
    setLoading(true);
    setError(null);
    setSignedUrl('');
    if (timeoutId) clearTimeout(timeoutId);
    // Set a 10s timeout
    const tId = setTimeout(() => {
      if (!didCancel) {
        setError('Failed to load image: request timed out');
        setLoading(false);
      }
    }, 15000);
    setTimeoutId(tId);

    try {
      // Process the image key
      let key = imageUrl;
      if (key.startsWith('http')) {
        const url = new URL(key);
        const pathParts = url.pathname.split('/');
        key = pathParts[pathParts.length - 1];
      } else if (key.includes('/')) {
        const pathParts = key.split('/');
        key = pathParts[pathParts.length - 1];
      }
      
      // Check if we already have this URL in cache
      const cachedUrl = imageCache.get(key);
      if (cachedUrl) {
        console.log('[ImageComponent] Using cached URL for:', key);
        if (!didCancel) {
          clearTimeout(tId);
          setSignedUrl(cachedUrl);
          setLoading(false);
          setError(null);
        }
        return;
      }
      
      console.log('[ImageComponent] Fetching signed URL for image key:', key);
      // Fetch the signed URL from the backend
      const url = await images.getSignedUrl(key);
      if (!didCancel) {
        clearTimeout(tId);
        setSignedUrl(url);
        setLoading(false);
        setError(null);
        
        // Store in cache for future use
        imageCache.set(key, url);
      }
    } catch (err) {
      if (!didCancel) {
        clearTimeout(tId);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setLoading(false);
      }
    }
    return () => { didCancel = true; if (timeoutId) clearTimeout(timeoutId); };
  };

  useEffect(() => {
    if (imageUrl) {
      fetchSignedUrlWithTimeout();
    } else {
      setLoading(false);
      setError('No image URL provided');
    }
    // Cleanup timeout on unmount
    return () => { if (timeoutId) clearTimeout(timeoutId); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl, retryCount]);

  if (loading) {
    return <Box sx={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress />
    </Box>;
  }

  if (error || !signedUrl) {
    return <Box sx={{ height: '200px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <Typography color="error" sx={{ mb: 2 }}>Failed to load image.</Typography>
      <Button variant="outlined" onClick={() => { setRetryCount(c => c + 1); setError(null); setLoading(true); }}>
        Retry
      </Button>
      {error && <Typography color="textSecondary" sx={{ mt: 1, fontSize: '0.9rem' }}>{error}</Typography>}
    </Box>;
  }

  return (
    <Box sx={{ 
      mb: 3, 
      overflow: 'hidden',
      maxWidth: '100%',
      maxHeight: '500px',
      display: 'flex',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      borderRadius: 2,
      padding: 2
    }}>
      <Box 
        component="img"
        src={signedUrl}
        alt="Question visual"
        sx={{
          maxWidth: '100%',
          maxHeight: '500px',
          objectFit: 'contain'
        }}
        onError={(e) => {
          console.error('[ImageComponent] Image failed to load:', signedUrl);
          setError(`Failed to load image: ${imageUrl}`);
        }}
      />
    </Box>
  );
};

const QuestionCard: React.FC<QuestionCardProps> = ({ 
  question, 
  onSubmit, 
  flowName, 
  isSubmitting = false,
  onProgressWithoutSubmitting
}) => {
  const { isMobile } = useResponsiveLayout();
  
  const navigate = useNavigate();
  const [response, setResponse] = useState<ResponsePayload>({ question: question.id as number });
  const [ratingValue, setRatingValue] = useState<number>(0);
  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
  const [booleanValue, setBooleanValue] = useState<boolean | null>(null);
  const [textValue, setTextValue] = useState<string>('');
  const [revealedOptions, setRevealedOptions] = useState<number[]>([]);
  const [customOtherValue, setCustomOtherValue] = useState<string>('');
  const [isSubmittingState, setIsSubmittingState] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isValidResponse, setIsValidResponse] = useState<boolean>(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [isPdfValid, setIsPdfValid] = useState<boolean>(false);
  const [showBooleanFeedback, setShowBooleanFeedback] = useState<boolean>(false);
  const submitButtonRef = useRef<HTMLDivElement>(null);
  
  // Bug report states
  const [isBugReportOpen, setIsBugReportOpen] = useState(false);
  const [bugFeedbackText, setBugFeedbackText] = useState('');
  const [isSubmittingBug, setIsSubmittingBug] = useState(false);
  const [bugReportSuccess, setBugReportSuccess] = useState(false);
  const [showThankYouDialog, setShowThankYouDialog] = useState(false);


  // Reset state when question changes
  useEffect(() => {
    console.log('[QuestionCard] Question changed:', {
      questionId: question.id,
      type: question.question_type_display
    });
    setRatingValue(0); // Reset to middle value
    setBooleanValue(null);
    setSelectedOptions([]);
    setTextValue('');
    setRevealedOptions([]);
    setCustomOtherValue('');
    setAudioBlob(null);
    setIsValidResponse(false);
    setIsPdfValid(false);
    setShowBooleanFeedback(false);
  }, [question.id]); // Only run when question.id changes

  // Show spinner if question is not available (loading or retrying)
  if (!question) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 400, width: '100%' }}>
        <CircularProgress size={64} />
      </Box>
    );
  }

  const handleReveal = async (optionId: number) => {
    if (!revealedOptions.includes(optionId)) {
      setRevealedOptions([...revealedOptions, optionId]);
    }
  };

  const handleSubmit = async (response: ResponsePayload) => {
    if (isSubmitting || isSubmittingState) return;
    
    console.log('[QuestionCard] Starting submission:', {
      questionId: question.id,
      type: question.question_type_display,
      hasAudioBlob: !!audioBlob,
      audioBlobSize: audioBlob?.size,
      hasTextValue: !!textValue,
      textLength: textValue?.length,
      response
    });
    
    setIsSubmittingState(true);
    setIsValidResponse(false);
    try {
      // Handle PDF questions
      if (question.question_type_display === 'pdf') {
        console.log('[QuestionCard] Submitting PDF response');
        await onSubmit({ question: question.id as number });
      } 
      // Handle audio responses
      else if ((question.question_type_display === 'audio_response' || 
                question.question_type_display === 'text_or_audio' || 
                question.question_type_display === 'text_or_audio_response') && 
                audioBlob) {
        console.log('[QuestionCard] Submitting audio response:', {
          questionId: question.id,
          hasAudio: !!audioBlob,
          hasText: !!textValue,
          textLength: textValue?.length,
          audioBlobSize: audioBlob.size
        });
        
        // Handle audio submission with transcription
        const result = await responses.submitResponseWithAudio(
          question.id as number,
          audioBlob,
          textValue // Pass the transcribed/edited text
        );
        
        console.log('[QuestionCard] Audio submission response:', {
          success: !!result?.data,
          data: result?.data
        });

        // Wait for the response data before calling onSubmit
        if (result?.data) {
          await onSubmit(result.data);
          setAudioBlob(null); // Clear the audio blob after successful submission
          setTextValue(''); // Clear the text value after successful submission
          console.log('[QuestionCard] Audio submission complete and state cleared');
          
          // After successful submission, check if it's the daily reflection flow
          if (flowName === 'daily_reflection') {
            navigate('/');
          }
          
          setIsValidResponse(true);
          return; // Return early to prevent double submission
        } else {
          throw new Error('No response data received from audio submission');
        }
      } 
      // Handle all other response types
      else {
        console.log('[QuestionCard] Submitting regular response:', {
          response,
          questionId: question.id
        });

        // Prepare the response payload based on question type
        let responseData: ResponsePayload = {
          question: question.id as number
        };

        const questionType = question.question_type_display;
        
        switch (questionType) {
          case 'rating':
            responseData.rating_value = ratingValue;
            break;
          case 'multiple_choice':
          case 'multiple_select':
          case 'drag_and_drop':
          case 'horizontal_drag_drop':
            responseData.selected_options = selectedOptions;
            
            // Add selected option text to text_value
            const selectedOptionTexts = selectedOptions.map(optionId => {
              const option = question.options?.find(opt => opt.id === optionId);
              return option?.front_text || '';
            }).filter(text => text !== '');

            // Check if an 'Other' option is selected
            const otherOption = question.options?.find(option => option.is_other);
            if (otherOption && (
                (questionType === 'multiple_choice' && selectedOptions[0] === otherOption.id) ||
                (questionType === 'multiple_select' && selectedOptions.includes(otherOption.id))
              )) {
              // Add the custom 'Other' value
              responseData.custom_other_value = customOtherValue;
              // Add custom value to text if it exists
              if (customOtherValue) {
                selectedOptionTexts.push(customOtherValue);
              }
            }

            // Join all selected options into a single text value
            responseData.text_value = selectedOptionTexts.join('; ');
            break;
          case 'boolean':
            responseData.text_value = booleanValue ? 'true' : 'false';
            break;
          case 'styled_boolean':
            responseData.text_value = booleanValue ? 'true' : 'false';
            break;
          case 'text':
          case 'free_response':
          case 'text_or_audio':
          case 'text_audio':
            responseData.text_value = textValue;
            break;
          case 'multiple_reveal':
            responseData.revealed_options = revealedOptions;
            break;
          case 'description':
          case 'radar_chart':
            // No additional data needed
            break;
          default:
            // Use the provided response data for any other types
            responseData = { ...response, question: question.id as number };
        }
        
        await onSubmit(responseData);
      }

      // After successful submission, check if it's the daily reflection flow
      if (flowName === 'daily_reflection') {
        navigate('/');
      }
    } catch (error) {
      console.error('[QuestionCard] Submission error:', error);
      setError(error instanceof Error ? error.message : 'Unknown error');
      throw error;
    } finally {
      setIsSubmittingState(false);
      setIsValidResponse(true);
    }
  };

  const checkIsValidResponse = (): boolean => {
    const questionType = question.question_type_display;
    
    switch (questionType) {
      case 'description':
      case 'image':  // Image type is always valid like description
        return true;
      case 'audio_response':
        return audioBlob !== null;
      case 'multiple_reveal':
        return true;
      case 'multiple_choice':
        // Check if an 'Other' option is selected
        if (selectedOptions.length === 1) {
          const otherOption = question.options?.find(option => option.is_other);
          if (otherOption && selectedOptions[0] === otherOption.id) {
            // If 'Other' is selected, require a custom value
            return customOtherValue.trim().length > 0;
          }
          return true; // Normal option selected
        }
        return false;
      case 'multiple_select':
        // Check if an 'Other' option is selected
        if (selectedOptions.length > 0) {
          const otherOption = question.options?.find(option => option.is_other);
          if (otherOption && selectedOptions.includes(otherOption.id)) {
            // If 'Other' is selected, require a custom value
            return customOtherValue.trim().length > 0;
          }
          return true; // Normal options selected
        }
        return false;
      case 'free_response':
        return textValue.trim().length > 0;
      case 'boolean':
        return booleanValue !== null;
      case 'styled_boolean':
        return booleanValue !== null;
      case 'text_or_audio_response':
      case 'text_or_audio':
      case 'text_audio':
        return audioBlob !== null || textValue.trim().length > 0;
      case 'text':
        return textValue.trim().length > 0;
      case 'rating':
        return ratingValue >= 1 && ratingValue <= 7;
      case 'drag_and_drop':
        return selectedOptions.length > 0;
      case 'horizontal_drag_drop':
        return selectedOptions.length > 0;
      case 'video_player':
        return isValidResponse;
      case 'pdf':
        return isPdfValid;
      case 'radar_chart':
        return true;
      default:
        return false;
    }
  };

  const renderQuestionContent = () => {
    const questionType = question.question_type_display;
    
    // Map text_audio to text_or_audio for compatibility
    if (questionType === 'text_audio') {
      console.log(`[QuestionCard] Mapped 'text_audio' to 'text_or_audio' for compatibility`);
    }
    
    // Debug the question type and visual component URL
    console.log(`[QuestionCard] Rendering question type: ${questionType}`);
    console.log(`[QuestionCard] Question has image URL: ${!!question.visual_component_url}`);
    console.log(`[QuestionCard] Image URL: ${question.visual_component_url || 'none'}`);
    
    // For description type with no text, don't render the content box
    // but we'll still show a Continue button below
    if (questionType === 'description' && (!question.text || question.text.trim() === '')) {
      return null;
    }
    
    switch (questionType) {
      case 'description':
      case 'image':  // Handle image type same as description
        return (
          <Box>
            <DescriptionQuestion question={question} />
          </Box>
        );
      case 'audio_response':
        return (
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper 
                elevation={0}
                sx={{ 
                  p: 3, 
                  bgcolor: 'transparent',
                  borderRadius: 0,
                  backdropFilter: 'none',
                  border: 'none',
                }}
              >
                <VoiceRecorder
                  onRecordingComplete={(blob, transcription) => {
                    if (blob === null) {
                      setAudioBlob(null);
                      setTextValue('');
                    } else {
                      setAudioBlob(blob);
                      if (transcription) {
                        setTextValue(transcription);
                      }
                    }
                  }}
                  isSubmitting={isSubmitting || isSubmittingState}
                />
              </Paper>
            </Box>
          </Box>
        );
      case 'multiple_reveal':
        return (
          <Box sx={{ width: '100%', bgcolor: 'transparent' }}>
            <Box sx={{ 
              display: 'grid', 
              gap: 2, 
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              width: '100%',
              my: 2
            }}>
              {(question.options ?? []).map((option) => (
                <RevealCard
                  key={option.id}
                  frontText={option.front_text || ''}
                  backText={option.back_text || ''}
                  isRevealed={revealedOptions.includes(option.id)}
                  onReveal={handleReveal}
                  optionId={option.id}
                />
              ))}
            </Box>
          </Box>
        );
      case 'multiple_choice':
        return (
          <Box sx={{ 
            width: '100%',
            bgcolor: 'transparent',
            p: 3
          }}>
            <MultipleChoiceQuestion
              question={question}
              selectedOption={selectedOptions[0]}
              onChange={(optionId) => setSelectedOptions([optionId])}
              customOtherValue={customOtherValue}
              onCustomOtherChange={setCustomOtherValue}
            />
          </Box>
        );
      case 'multiple_select':
        return (
          <Box sx={{ 
            width: '100%',
            bgcolor: 'transparent',
            p: 3
          }}>
            <MultipleSelectQuestion
              question={question}
              selectedOptions={selectedOptions}
              onChange={setSelectedOptions}
              customOtherValue={customOtherValue}
              onCustomOtherChange={setCustomOtherValue}
            />
          </Box>
        );
      case 'boolean':
        console.log('[QuestionCard] Rendering boolean question');
        console.log('[QuestionCard] Boolean value:', booleanValue);
        return (
          <Box sx={{ 
            width: '100%',
            bgcolor: 'transparent',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
              <Button
                variant="outlined"
                sx={{
                  width: '100%',
                  padding: (theme) => theme.spacing(2),
                  borderRadius: (theme) => theme.spacing(1),
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 'normal',
                  justifyContent: 'center',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  backgroundColor: booleanValue === true ? '#374fff' : 'rgba(255, 255, 255, 0.05)',
                  color: booleanValue === true ? '#ffffff' : 'text.primary',
                  '&:hover': {
                    backgroundColor: booleanValue === true ? '#2e41cc' : 'rgba(255, 255, 255, 0.1)',
                  },
                }}
                onClick={() => {
                  setBooleanValue(true);
                }}
              >
                Yes
              </Button>
              <Button
                variant="outlined"
                sx={{
                  width: '100%',
                  padding: (theme) => theme.spacing(2),
                  borderRadius: (theme) => theme.spacing(1),
                  textTransform: 'none',
                  fontSize: '1rem',
                  fontWeight: 'normal',
                  justifyContent: 'center',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  backgroundColor: booleanValue === false ? '#e66ca8' : 'rgba(255, 255, 255, 0.05)',
                  color: booleanValue === false ? '#ffffff' : 'text.primary',
                  '&:hover': {
                    backgroundColor: booleanValue === false ? '#c25a8c' : 'rgba(255, 255, 255, 0.1)',
                  },
                }}
                onClick={() => {
                  setBooleanValue(false);
                }}
              >
                No
              </Button>
            </Box>
          </Box>
        );
      case 'styled_boolean':
        return (
          <Box sx={{ 
            width: '100%',
            bgcolor: 'transparent',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2
          }}>
            <StyledBooleanQuestion
              question={question}
              booleanValue={booleanValue}
              onChange={(value) => {
                setBooleanValue(value);
                setShowBooleanFeedback(true);
              }}
              showFeedback={showBooleanFeedback}
            />
          </Box>
        );
      case 'free_response':
        return (
          <Box sx={{ 
            width: '100%',
            bgcolor: 'transparent',
            p: 3
          }}>
            <FreeResponseQuestion
              question={question}
              value={textValue}
              onChange={setTextValue}
            />
          </Box>
        );
      case 'text':
      case 'text_or_audio_response':
      case 'text_or_audio':
      case 'text_audio':
        return (
          <Box sx={{ 
            width: '100%',
            bgcolor: 'rgba(38, 38, 38, 0.6)',
            borderRadius: 2,
            border: '1px solid rgba(255, 255, 255, 0.15)',
            p: 3
          }}>
            <TextOrAudioResponseQuestion
              question={question}
              textValue={textValue}
              audioBlob={audioBlob}
              onTextChange={setTextValue}
              onAudioChange={setAudioBlob}
              isSubmitting={isSubmitting || isSubmittingState}
            />
          </Box>
        );
      case 'rating':
        return (
          <Box sx={{ 
            width: '100%',
            bgcolor: 'transparent',
            p: 3
          }}>
            <RatingQuestion
              question={question}
              value={ratingValue}
              onChange={setRatingValue}
            />
          </Box>
        );
      case 'drag_and_drop':
        return (
          <Box sx={{ 
            width: '100%',
            bgcolor: 'transparent',
            p: 3
          }}>
            <DragAndDropQuestion
              question={question}
              selectedOptions={selectedOptions}
              onChange={setSelectedOptions}
            />
          </Box>
        );
      case 'horizontal_drag_drop':
        return (
          <Box sx={{ 
            width: '100%',
            bgcolor: 'transparent',
            p: 3
          }}>
            <HorizontalDragDropQuestion
              question={question}
              selectedOptions={selectedOptions}
              onChange={setSelectedOptions}
            />
          </Box>
        );
      case 'video_player':
        return (
          <Box sx={{ 
            width: '100%',
            bgcolor: 'transparent',
            p: 3
          }}>
            <VideoPlayerQuestion
              question={question}
              isSubmitting={isSubmitting || isSubmittingState}
              onValidationChange={(isValid) => setIsValidResponse(isValid)}
            />
          </Box>
        );
      case 'pdf':
        return (
          <Box sx={{ 
            width: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            bgcolor: 'transparent',
            mb: 4
          }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', mb: 3 }}>
              {question.text}
            </Typography>
            <Box sx={{ width: '100%', maxWidth: '70%' }}>
              <PDFQuestion
                question={question}
                onValidationChange={(isValid) => {
                  setIsValidResponse(isValid);
                  setIsPdfValid(isValid);
                }}
                isSubmitting={isSubmitting || isSubmittingState}
              />
            </Box>
          </Box>
        );
      case 'callback_summary':
        return (
          <Box sx={{ width: '100%' }}>
            <CallbackSummaryQuestion
              question={question}
              onSubmit={onSubmit}
              isSubmitting={isSubmitting || isSubmittingState}
            />
          </Box>
        );
      case 'radar_chart':
        return (
          <Box sx={{ 
            width: '100%',
            bgcolor: 'transparent',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3
          }} {...getTestId(`radar-chart-container-${question.id}`)}>
            {question.image_url && (
              <Box sx={{ maxWidth: '100%', mb: 3 }}>
                <ImageComponent imageUrl={question.image_url} />
              </Box>
            )}
            <Box sx={{ width: '100%', minWidth: '600px', height: 500, maxWidth: '90vw' }}>
              <CustomRadarChart />
            </Box>
          </Box>
        );
      default:
        console.warn(`[QuestionCard] Unknown question type: ${questionType}`);
        return null;
    }
  };

  const isSubmitDisabled = () => {
    return !checkIsValidResponse() || isSubmitting || isSubmittingState;
  };

  const renderSubmitButton = () => {
    const questionType = question.question_type_display;
    let buttonText = 'Submit';
    if (questionType === 'callback_summary') {
      buttonText = 'Save & Continue';
    } else if (questionType === 'pdf' || 
               questionType === 'description' || 
               questionType === 'image') {
      buttonText = 'Continue';
    }
    
    // Check if this question has already been submitted
    // We can determine this from the isValidResponse state which is set to true after submission
    const hasBeenSubmitted = isValidResponse;
    
    return (
      <SubmitButton
        onSubmit={() => handleSubmit({ question: question.id as number })}
        isDisabled={isSubmitDisabled()}
        isLoading={isSubmitting || isSubmittingState}
        buttonText={buttonText}
        isAlreadySubmitted={hasBeenSubmitted}
      />
    );
  };

  const handleBugReportSubmit = async () => {
    if (!bugFeedbackText.trim()) {
      return;
    }

    setIsSubmittingBug(true);
    try {
      // Prepare the current response data
      const currentResponse = {
        question: question.id as number,
        rating_value: ratingValue,
        text_value: textValue,
        boolean_value: booleanValue,
        selected_options: selectedOptions,
        custom_other_value: customOtherValue,
      };

      await submitBugReport({
        question_id: question.id as number,
        submitted_response: currentResponse,
        feedback_text: bugFeedbackText,
      });

      setBugReportSuccess(true);
      setIsBugReportOpen(false);
      setBugFeedbackText('');
      
      // Show thank you dialog before progressing to the next question
      if (onProgressWithoutSubmitting) {
        setShowThankYouDialog(true);
        
        // Add a delay before progressing to the next question to allow user to see the thank you message
        setTimeout(() => {
          setShowThankYouDialog(false);
          onProgressWithoutSubmitting();
        }, 2000); // Longer delay to ensure user sees the thank you message
      }
    } catch (err) {
      console.error('Error submitting bug report:', err);
      setError('Failed to submit bug report. Please try again.');
    } finally {
      setIsSubmittingBug(false);
    }
  };

  return (
    <StyledCard 
      isPdf={question.question_type_display === 'pdf'}
      {...getTestId(`question-card-${question.id}`)}
    >
      {/* Don't show question text for PDF questions as it's shown in the PDF component */}
      {(question.question_type_display !== 'pdf' && question.question_type_display !== 'callback_summary') && (
        <QuestionText isMobile={isMobile}>
          {question.text}
        </QuestionText>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Display image if visual_component_url is provided */}
      {question.visual_component_url && (
        <Box sx={{ mb: 3, width: '100%' }}>
          {/* Add debugging information */}
          <span style={{ display: 'none' }}>
            {(() => {
              console.log('Rendering image with URL:', question.visual_component_url);
              return null;
            })()}
          </span>
          <ImageComponent imageUrl={question.visual_component_url} />
        </Box>
      )}
      
      {/* Only render the question content if there's content to show */}
      {renderQuestionContent()}
      
      {/* Show submit/continue button for all questions except callback_summary */}
      {question.question_type_display !== 'callback_summary' && (
        <Box ref={submitButtonRef} sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          {renderSubmitButton()}
        </Box>
      )}
      
      {/* Bug Report Button - Positioned in bottom right corner */}
      <Box 
        sx={{ 
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000
        }}
      >
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={() => setIsBugReportOpen(true)}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '0.875rem',
            backgroundColor: '#62c4ba',
            color: '#ffffff',
            borderColor: '#62c4ba',
            '&:hover': {
              backgroundColor: '#52a59d',
              borderColor: '#52a59d',
            },
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          Submit Feedback
        </Button>
      </Box>

      {/* Bug Report Dialog */}
      <Dialog 
        open={isBugReportOpen} 
        onClose={() => !isSubmittingBug && setIsBugReportOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Submit Feedback</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please describe the issue you're experiencing with this question:
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Issue Description"
              value={bugFeedbackText}
              onChange={(e) => setBugFeedbackText(e.target.value)}
              disabled={isSubmittingBug}
              placeholder="Describe what's wrong or confusing about this question..."
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setIsBugReportOpen(false)} 
            disabled={isSubmittingBug}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleBugReportSubmit}
            variant="contained" 
            color="primary"
            disabled={!bugFeedbackText.trim() || isSubmittingBug}
          >
            {isSubmittingBug ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Submit Report'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={bugReportSuccess}
        autoHideDuration={6000}
        onClose={() => setBugReportSuccess(false)}
        message="Bug report submitted successfully"
      />
      
      {/* Thank You Dialog */}
      <Dialog
        open={showThankYouDialog}
        aria-labelledby="thank-you-dialog-title"
        aria-describedby="thank-you-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 2,
            maxWidth: '400px'
          }
        }}
      >
        <DialogTitle id="thank-you-dialog-title" sx={{ textAlign: 'center' }}>
          Thank You For Your Feedback!
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="thank-you-dialog-description" sx={{ textAlign: 'center' }}>
            We appreciate your input. You will now be moved to the next question.
          </DialogContentText>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={30} sx={{ color: '#62c4ba' }} />
          </Box>
        </DialogContent>
      </Dialog>
    </StyledCard>
  );
};

export default QuestionCard;